import React, { useEffect, useRef, useState } from "react";
import AppLayout from "@/Layouts/AppLayout";
import OrganizationLayout from "@/Layouts/OrganizationLayout";
import { Inertia } from "@inertiajs/inertia";
import "react-datepicker/dist/react-datepicker.css";
import LayoutIcon from "@/Components/LayoutIcon";
import { Folder as FolderIcon } from "@/Icons";
import { atom, useAtom } from "jotai";
import { debounce } from "lodash";
import { SCOPE_OPTIONS } from "@/Enums/scopes";
import {
  LabeledInput,
  LabeledMultiSelectAutocomplete,
  Select,
} from "@/Components/Form";
import { Plus, Search } from "react-feather";
import { Dropdown, EmptyState } from "@/Components";
import Row from "@/Components/Library/Row";
import { InertiaLink, usePage } from "@inertiajs/inertia-react";
import { LoadingProvider, useLoading } from "@/Contexts/LoadingContext";
import { useSetAtom } from "jotai/index";
import { isNewActivityOpenAtom } from "@/Pages/Reporting/Owner/Index";
import NewActivityChooseType from "@/Components/Team/NewActivityChooseType";

const searchAtom = atom("");
const sortAtom = atom("created.asc");
const scopesAtom = atom(null);
const tagsAtom = atom(null);

export default function Index({ questions, tags, teams, ppp }) {
  const [questionEmissions, setQuestionEmissions] = useState(null);
  const { setIsLoaded } = useLoading();

  useEffect(() => {
    if (questions.data.length === 0 || teams.data.length === 0) {
      setIsLoaded(true);
      return;
    }

    if (!questionEmissions) {
      setIsLoaded(false);

      // Fetch data for all questions
      axios
        .post(route("teams.questions.emissions.data"), {
          questions: questions.data.map((q) => q.id),
          teams: teams.data.map((team) => team.id),
        })
        .then((res) => {
          setQuestionEmissions(res.data);
          setIsLoaded(true);
        });
    }
  }, [questions]);

  const createdQuestionId =
    usePage().props?.custom_response_data?.created_question_id || false;
  function showFilteredQuestion(question, id) {
    return (
      <Row
        key={`question_${id}`}
        question={question}
        emission={questionEmissions?.[question.id]}
        isNew={createdQuestionId === id}
      />
    );
  }

  const questionsSortedByNewlyCreated = (createdQuestionId) => {
    return questions.data;
    // if (!createdQuestionId) {
    //   team.questions
    // }
    // return team.questions.sort(q => createdQuestionId === q.id ? -1 : 1)
  };

  const questionComponents = questionsSortedByNewlyCreated(createdQuestionId)
    .map((question, i) => showFilteredQuestion(question, question.id))
    .filter((n) => n);

  const setNewActivityDialogOpen = useSetAtom(isNewActivityOpenAtom);

  return (
    <>
      <ActivityFilter tagData={tags.data} />
      {questionComponents.length === 0 ? (
        <EmptyState
          title="No activities in the library"
          text="Create an activity to start reporting"
          buttonText="New activity"
          buttonOnClick={() => setNewActivityDialogOpen(true)}
        />
      ) : (
        <div className="grid gap-2">
          {questionComponents}
          {pagination(questions, "", ppp)}
        </div>
      )}
    </>
  );
}

const ActivityFilter = ({ tagData }) => {
  const [search, setSearch] = useAtom(searchAtom);
  const [sort, setSort] = useAtom(sortAtom);
  const [scopes, setScopes] = useAtom(scopesAtom);
  const [tags, setTags] = useAtom(tagsAtom);

  const sortOptions = [
    { value: "title.asc", label: "Name A-Ö" },
    { value: "title.desc", label: "Name Ö-A" },
    { value: "created_at.desc", label: "Newest" },
    { value: "created_at.asc", label: "Oldest" },
    { value: "updated_at.desc", label: "Updated most recent" },
    { value: "updated_at.asc", label: "Updated least recent" },
  ];

  const tagOptions = tagData.map((tag) => {
    return {
      value: String(tag.id),
      label: tag.name,
    };
  });

  const scopeRef = useRef(null);
  const tagRef = useRef(null);

  const handleSearchAndSort = React.useRef(
    debounce((search, sort, scopes, tags) => {
      const data = {
        search,
        sort,
        scopes: scopes?.join(","),
        tags: tags?.join(","),
      };

      if (data.tags === "" || data.scopes === "") {
        data.page = 1;
      }

      Inertia.reload({
        only: ["questions"],
        data,
      });
    }, 500),
  ).current;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (!params.get("scopes") || scopes === null) {
      const initialSearch = params.get("search") || "";
      const initialSort = params.get("sort") || "";
      const initialScopes = params.get("scopes")
        ? params.get("scopes").split(",")
        : ["other", ...SCOPE_OPTIONS.map((s) => s.value)];
      const initialTags = params.get("tags")
        ? params.get("tags").split(",")
        : ["other", ...tagOptions.map((s) => s.value)];

      setSearch(initialSearch);
      setSort(initialSort);
      setScopes(initialScopes);
      setTags(initialTags);

      scopeRef.current.setStateOptions((prev) => {
        return prev.map((scope) => {
          return {
            ...scope,
            checked: initialScopes.includes(scope.value),
          };
        });
      });

      tagRef.current.setStateOptions((prev) => {
        return prev.map((tag) => {
          return {
            ...tag,
            checked: initialTags.includes(tag.value),
          };
        });
      });

      refreshData(initialSearch, initialSort, initialScopes, initialTags);
    }
  }, []);

  const refreshData = (search, sort, scopes, tags) => {
    handleSearchAndSort.cancel();
    handleSearchAndSort(search, sort, scopes, tags);
  };

  return (
    <div className="mb-4 flex flex-col items-end justify-between gap-4 sm:flex-row sm:items-center">
      <div className="flex w-full flex-col gap-4 sm:w-auto sm:flex-row sm:items-end">
        <LabeledMultiSelectAutocomplete
          title="Categories and modules"
          className="w-full sm:w-auto"
          ref={tagRef}
          placeholder="All"
          options={[{ value: "other", label: "Other" }, ...tagOptions]}
          defaultValue={tags || []}
          onSelect={(val) => {
            const values = val.map((v) => v.value);
            setTags(values);
            refreshData(search, sort, scopes, values);
          }}
        />

        <LabeledMultiSelectAutocomplete
          title="Scopes"
          className="w-full sm:w-auto"
          ref={scopeRef}
          placeholder="Scopes"
          options={[{ value: "other", label: "Other" }, ...SCOPE_OPTIONS]}
          defaultValue={scopes || []}
          onSelect={(val) => {
            const values = val.map((v) => v.value);
            setScopes(values);
            refreshData(search, sort, values, tags);
          }}
        />

        <LabeledInput
          title="Search activity"
          value={search}
          type="search"
          placeholder="Search"
          className="w-full sm:w-auto"
          unit={search === "" ? <Search className="relative -top-1" /> : null}
          onChange={(e) => {
            const value = e.target.value;
            setSearch(e.target.value);
            refreshData(value, sort, scopes, tags);
          }}
        />
      </div>

      <Dropdown
        options={sortOptions}
        title="Sort on"
        placeholder="Name A-Ö"
        onSelect={(value) => {
          setSort(value);
          refreshData(search, value, scopes, tags);
        }}
      />
    </div>
  );
};

const getActiveLink = (l) => {
  return l.filter((l) => l.active)[0]?.url || false;
};
function pagination(data, ti, ppp) {
  const active_url = getActiveLink(data?.meta?.links || []);
  const mp = data?.meta?.current_page || 1;

  if (!data?.meta) {
    return "";
  }

  return (
    <div className="flex flex-row items-center justify-between py-4">
      {data.meta.last_page > 1 && (
        <div className="flex flex-shrink flex-grow-0">
          {data.meta.links.map((l, i) => (
            <div key={i}>
              {!l.url && <span className="mr-2">{l.label}</span>}
              {l.url && (
                <InertiaLink
                  preserveScroll
                  className={`mr-2 px-3 py-2 ${l.active ? "bg-primary text-white" : ""}`}
                  href={l.url}
                >
                  {l.label}
                </InertiaLink>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-row items-center justify-self-end">
        {/* <span className="mr-2">Per page</span> */}
        <Select
          className="w-32"
          options={[
            { value: "15", label: "Visa 15" },
            { value: "25", label: "Visa 25" },
            { value: "50", label: "Visa 50" },
            { value: "100", label: "Visa 100" },
          ]}
          value={ppp}
          onChange={(e) => {
            Inertia.get(active_url + `&${ti}=${mp}`, {
              // preserveScroll: true,
              ppp: e.target.value,
            });
          }}
        />
      </div>
    </div>
  );
}

const HeaderActions = () => {
  const setNewActivityDialogOpen = useSetAtom(isNewActivityOpenAtom);

  return (
    <button
      className="btn-blue shrink-0 grow-0"
      onClick={() => setNewActivityDialogOpen(true)}
    >
      New activity
    </button>
  );
};

Index.layout = (page) => {
  // Inertia.visit('library/activity/create') }}

  return (
    <AppLayout title="Activity Library">
      <LoadingProvider>
        <OrganizationLayout
          header="Activity Library"
          byline="See all activities in your collection, and add new ones"
          action={<HeaderActions />}
          icon={
            <LayoutIcon>
              <FolderIcon size={40} className="text-black" />
            </LayoutIcon>
          }
        >
          {page}
        </OrganizationLayout>
      </LoadingProvider>
      <LoadingProvider>
        <NewActivityChooseType source="library" />
      </LoadingProvider>
    </AppLayout>
  );
};
